import Service, { service } from '@ember/service';
import { action } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import { variation } from 'ember-launch-darkly';

export const SCREEN_LOCKED_KEY = 'my-phorest:screen-locked';
export const USER_PIN_IDENTIFIED_KEY = 'my-phorest:user-pin-identified';

export default class AutoLogoutService extends Service {
  @service('browser/local-storage') localStorage;
  @service('browser/document') document;
  @service('browser/session-storage') sessionStorage;
  @service('browser/window') window;
  @service access;
  @service timers;
  @service swingBridge;
  @service userIdleness;
  @service session;
  @service clockInOut;
  @service router;
  @service zendesk;

  logoutTimeInMinutes = null;
  timerId = null;
  #isLocked;

  get isUserIdentifiedByPin() {
    if (!variation('release-pin-after-login')) return true;

    if (this.session.isGlobalUser) return true;

    return JSON.parse(
      this.sessionStorage.getItem(USER_PIN_IDENTIFIED_KEY) ?? 'false'
    );
  }

  set isUserIdentifiedByPin(value) {
    this.sessionStorage.setItem(USER_PIN_IDENTIFIED_KEY, JSON.stringify(value));
  }

  set isLocked(value) {
    if (this.session.isGlobalUser && !!value) return;
    this.#isLocked = value;
    this.localStorage.setItem(SCREEN_LOCKED_KEY, JSON.stringify(value));
  }

  get isLocked() {
    if (this.session.isGlobalUser) return false;

    if (typeof this.#isLocked !== 'undefined') {
      return this.#isLocked;
    }

    let value = JSON.parse(
      this.localStorage.getItem(SCREEN_LOCKED_KEY) ?? 'false'
    );

    return !!value;
  }

  activate(logoutTime) {
    if (this.swingBridge.isEmbeddedInSwing) return;

    this.logoutTimeInMinutes = logoutTime;

    this.timerId = this.timers.registerTimer({
      action: this.checkUserIdleness,
      every: 5_000,
      destroyable: this,
    });

    if (this.isLocked) {
      this.lockScreen.perform();
    }
  }

  deactivate() {
    if (this.timerId) {
      this.timers.unregisterTimer(this.timerId);
      this.timerId = null;
    }
  }

  get logoutTimeInSeconds() {
    return this.logoutTimeInMinutes * 60;
  }

  reset() {
    this.isLocked = false;
    this.isUserIdentifiedByPin = false;
  }

  @action
  checkUserIdleness() {
    if (!this.logoutTimeInMinutes) return;
    if (this.isLocked) return;

    const idleTime = this.userIdleness.idleTime({ granularity: 'seconds' });
    if (idleTime >= this.logoutTimeInSeconds) {
      this.lockScreen.perform();
    }
  }

  @dropTask
  *lockScreen() {
    if (this.session.isGlobalUser) return;

    this.isLocked = true;
    this.isUserIdentifiedByPin = false;
    this.zendesk.logout();

    yield this.access.openPinPadAndSwitchUser({
      cancelDisabled: true,
      hideBackground: true,
      onCancel: this.lockScreen.perform,
    });

    this.isLocked = false;
    this.isUserIdentifiedByPin = true;

    if (this.session.branch.staffMemberSettings?.clockInOutEnabled) {
      this.clockInOut.checkClockInOut(this.session.currentStaffMember, {
        showOnlyWhenClockedOut: true,
      });
    }
  }
}
